'use client'

import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Listbox } from '@headlessui/react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import Image from 'next/image'
import { useLanguage } from '@/contexts/LanguageContext'
import { transformationsTranslations } from '@/translations/transformations'

const transformations = [
  {
    id: 'modern-garden',
    beforeImage: '/images/transformations/before-1.webp',
    afterImage: '/images/transformations/after-1.webp',
  },
  {
    id: 'family-garden',
    beforeImage: '/images/transformations/before-2.webp',
    afterImage: '/images/transformations/after-2.webp',
  },
  {
    id: 'urban-oasis',
    beforeImage: '/images/transformations/before-3.webp',
    afterImage: '/images/transformations/after-3.webp',
  }
]

export default function Transformations() {
  const [activeCategory, setActiveCategory] = useState(transformations[0].id)
  const [sliderPosition, setSliderPosition] = useState(50)
  const containerRef = useRef<HTMLDivElement>(null)
  const { language } = useLanguage()
  const t = transformationsTranslations[language]

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const categories = [...new Set(transformations.map(t => t.id))]

  const filteredTransformations = transformations.filter(t => t.id === activeCategory)

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!containerRef.current) return

    const container = containerRef.current
    const rect = container.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percentage = (x / rect.width) * 100
    setSliderPosition(Math.min(Math.max(percentage, 0), 100))

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      if (!containerRef.current) return

      const container = containerRef.current
      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const percentage = (x / rect.width) * 100
      setSliderPosition(Math.min(Math.max(percentage, 0), 100))
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    if (!containerRef.current) return

    const container = containerRef.current
    const rect = container.getBoundingClientRect()
    const x = e.touches[0].clientX - rect.left
    const percentage = (x / rect.width) * 100
    setSliderPosition(Math.min(Math.max(percentage, 0), 100))

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      if (!containerRef.current) return

      const container = containerRef.current
      const rect = container.getBoundingClientRect()
      const x = e.touches[0].clientX - rect.left
      const percentage = (x / rect.width) * 100
      setSliderPosition(Math.min(Math.max(percentage, 0), 100))
    }

    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }

    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleTouchEnd)
  }

  useEffect(() => {
    return () => {
      // Clean up any remaining event listeners on unmount
      document.removeEventListener('mousemove', () => {})
      document.removeEventListener('mouseup', () => {})
      document.removeEventListener('touchmove', () => {})
      document.removeEventListener('touchend', () => {})
    }
  }, [])

  return (
    <section id="transformations" className="py-16 bg-gradient-to-b from-white to-gray-50 scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-section font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-large text-gray-600">
            {t.subtitle}
          </p>
        </div>

        {/* Mobile Dropdown */}
        <div className="lg:hidden relative mb-8">
          <Listbox value={activeCategory} onChange={setActiveCategory}>
            {({ open }: { open: boolean }) => (
              <div>
                <Listbox.Button className={`w-full p-4 text-left bg-white rounded-xl shadow-sm focus:outline-none transition-all duration-200 border ${open ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-300'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-gray-900">{t.transformations[activeCategory].category}</span>
                    <ChevronDown className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  </div>
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-sm border border-gray-100 max-h-60 overflow-auto focus:outline-none">
                  {Object.keys(t.transformations).map(category => (
                    <Listbox.Option
                      key={category}
                      value={category}
                      className={({ active }: { active: boolean }) =>
                        `relative cursor-pointer select-none px-6 py-4 ${active ? 'bg-gray-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
                      }
                    >
                      {({ selected }: { selected: boolean }) => (
                        <span className={`block text-lg font-medium ${selected ? 'text-gray-900' : 'text-gray-500'}`}>{t.transformations[category].category}</span>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            )}
          </Listbox>
        </div>

        {/* Desktop Category Navigation */}
        <div className="hidden lg:flex flex-wrap justify-center gap-4 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 border focus:outline-none ${
                activeCategory === category
                  ? 'bg-gray-900 text-white shadow-lg border-gray-900'
                  : 'bg-white text-gray-500 border-gray-200 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {t.transformations[category].category}
            </button>
          ))}
        </div>

        {/* Transformations Grid */}
        <div className="grid grid-cols-1 gap-12">
          {filteredTransformations.map((transformation) => (
            <motion.div
              key={transformation.id}
              ref={ref}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100"
            >
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.transformations[transformation.id].title}</h3>
                <p className="text-lg text-gray-600 mb-6">{t.transformations[transformation.id].description}</p>

                {/* Before/After Slider */}
                <div className="relative h-[400px] mb-8 rounded-xl overflow-hidden">
                  <div
                    ref={containerRef}
                    className="relative w-full h-full cursor-ew-resize"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  >
                    {/* Before Image */}
                    <div className="absolute inset-0">
                      <Image
                        src={transformation.beforeImage}
                        alt={`Before - ${t.transformations[transformation.id].title}`}
                        fill
                        loading="lazy"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover object-center"
                        priority={false}
                        quality={85}
                      />
                    </div>
                    {/* After Image with Slider */}
                    <div
                      className="absolute inset-0 overflow-hidden"
                      style={{
                        clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                        width: '100%'
                      }}
                    >
                      <Image
                        src={transformation.afterImage}
                        alt={`After - ${t.transformations[transformation.id].title}`}
                        fill
                        loading="lazy"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover object-center"
                        priority={false}
                        quality={85}
                      />
                    </div>
                    {/* Slider Control */}
                    <div
                      className="absolute inset-y-0"
                      style={{ left: `${sliderPosition}%` }}
                    >
                      <div className="absolute inset-y-0 -ml-px w-0.5 bg-white shadow-lg" />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-ew-resize"
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                      >
                        <div className="flex items-center justify-center w-full h-full">
                          <ChevronLeft className="w-3 h-3 text-gray-600" />
                          <ChevronRight className="w-3 h-3 text-gray-600" />
                        </div>
                      </div>
                    </div>
                    <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {t.before}
                    </div>
                    <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {t.after}
                    </div>
                  </div>
                </div>

                {/* Challenges & Solutions */}
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">{t.challenges}</h4>
                    <ul className="space-y-3">
                      {t.transformations[transformation.id].challenges.map((challenge, index) => (
                        <li key={index} className="flex items-start text-gray-600">
                          <svg className="w-5 h-5 text-gray-600 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          {challenge}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">{t.solutions}</h4>
                    <ul className="space-y-3">
                      {t.transformations[transformation.id].solutions.map((solution, index) => (
                        <li key={index} className="flex items-start text-gray-600">
                          <svg className="w-5 h-5 text-gray-600 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
} 